import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { Button } from 'components/index';
import CustomizedTextField from '../project/CustomizedTextField';
import NumericFormatInput from '../general/NumericFormatInput';
import DynamicDatePicker from '../common/DynamicDatePicker';
import { BaseInvoiceDTO, EditInvoiceValues, InvoiceHistoryField } from './invoice.type';
import { formatInvoiceHistoryValue, INVOICE_HISTORY_FIELD_LABELS } from './invoice.util';

interface EditPastInvoiceDialogProps {
  open: boolean;
  invoice: BaseInvoiceDTO | null;
  invoices: BaseInvoiceDTO[];
  onClose: () => void;
  onSave: (invoiceId: string, values: EditInvoiceValues) => Promise<void>;
}

type FormValues = Omit<EditInvoiceValues, 'changeReason' | 'comment'>;

const toFormValues = (invoice: BaseInvoiceDTO): FormValues => ({
  number: invoice.invoice.number || '',
  invoiceDate: (invoice.invoice.invoiceDate || '').split('T')[0],
  invoiceAmount: invoice.invoice.invoiceAmount || 0,
  paidAmount: invoice.invoice.paidAmount || 0,
});

const FIELD_KEYS: Partial<Record<InvoiceHistoryField, keyof FormValues>> = {
  Number: 'number',
  InvoiceDate: 'invoiceDate',
  InvoiceAmount: 'invoiceAmount',
  PaidAmount: 'paidAmount',
};
const EDITABLE_FIELDS = Object.keys(FIELD_KEYS) as InvoiceHistoryField[];

const EditPastInvoiceDialog: React.FC<EditPastInvoiceDialogProps> = ({ open, invoice, invoices, onClose, onSave }) => {
  const [values, setValues] = useState<FormValues | null>(null);
  const [changeReason, setChangeReason] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open && invoice) {
      setValues(toFormValues(invoice));
      setChangeReason('');
    }
  }, [open, invoice]);

  const original = useMemo(() => (invoice ? toFormValues(invoice) : null), [invoice]);

  const changedFields = useMemo(() => {
    if (!values || !original) return [];
    return EDITABLE_FIELDS.filter(field => {
      const key = FIELD_KEYS[field] as keyof FormValues;
      return String(original[key] ?? '').trim() !== String(values[key] ?? '').trim();
    });
  }, [values, original]);

  if (!invoice || !values || !original) return null;

  const trimmedNumber = values.number.trim();
  const isDuplicateNumber = invoices.some(inv => inv.id !== invoice.id && inv.invoice.number?.trim() === trimmedNumber);
  const errors = {
    number: !trimmedNumber ? 'Required' : isDuplicateNumber ? 'Invoice number already exists' : '',
    invoiceDate: !values.invoiceDate ? 'Required' : '',
    invoiceAmount: values.invoiceAmount <= 0 ? 'Must be greater than 0' : '',
    paidAmount: values.paidAmount > values.invoiceAmount ? 'Cannot exceed amount due' : '',
  };
  const hasErrors = Object.values(errors).some(Boolean);
  const canSave = !isSaving && !hasErrors && changedFields.length > 0;

  const setField = <K extends keyof FormValues>(key: K, value: FormValues[K]) =>
    setValues(prev => (prev ? { ...prev, [key]: value } : prev));

  const handleSave = async () => {
    if (!canSave) return;
    setIsSaving(true);
    try {
      await onSave(invoice.id, { ...values, number: trimmedNumber, changeReason: changeReason.trim() || undefined });
      toast.success(`Invoice ${trimmedNumber} updated`);
      onClose();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to update invoice');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth='md' fullWidth>
      <DialogTitle>Edit Invoice {original.number}</DialogTitle>
      <IconButton onClick={onClose} disabled={isSaving} style={{ position: 'absolute', right: 8, top: 8 }}>
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <Alert severity='info' sx={{ mb: 2, fontSize: '0.8rem' }}>
          Changes to past invoices are recorded in the invoice history with your name and the time of the edit.
        </Alert>
        <Grid container spacing={2}>
          <Grid size={{ xs: 3 }}>
            <Typography variant='body2'>Invoice # *</Typography>
            <CustomizedTextField
              value={values.number}
              onChange={e => setField('number', e.target.value)}
              fullWidth
              size='small'
              error={!!errors.number}
              helperText={errors.number}
            />
          </Grid>
          <Grid size={{ xs: 3 }}>
            <Typography variant='body2' color={errors.invoiceDate ? 'error' : 'inherit'}>
              Date *
            </Typography>
            <DynamicDatePicker
              fieldName='invoiceDate'
              value={values.invoiceDate || null}
              onChange={(_, value) => setField('invoiceDate', value || '')}
            />
          </Grid>
          <Grid size={{ xs: 3 }}>
            <Typography variant='body2'>Amount Due *</Typography>
            <CustomizedTextField
              value={values.invoiceAmount || ''}
              onChange={e => setField('invoiceAmount', parseFloat(e.target.value) || 0)}
              InputProps={{ inputComponent: NumericFormatInput as any }}
              fullWidth
              size='small'
              error={!!errors.invoiceAmount}
              helperText={errors.invoiceAmount}
            />
          </Grid>
          <Grid size={{ xs: 3 }}>
            <Typography variant='body2'>Amount Paid</Typography>
            <CustomizedTextField
              value={values.paidAmount || ''}
              onChange={e => setField('paidAmount', parseFloat(e.target.value) || 0)}
              InputProps={{ inputComponent: NumericFormatInput as any }}
              fullWidth
              size='small'
              error={!!errors.paidAmount}
              helperText={errors.paidAmount}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant='body2'>Reason for change</Typography>
            <CustomizedTextField
              value={changeReason}
              onChange={e => setChangeReason(e.target.value)}
              placeholder='e.g., Corrected amount per client remittance'
              fullWidth
              size='small'
            />
          </Grid>
        </Grid>

        {changedFields.length > 0 && (
          <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, backgroundColor: 'grey.100' }}>
            <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary' }}>
              CHANGES
            </Typography>
            {changedFields.map(field => (
              <Typography key={field} variant='body2' sx={{ mt: 0.5 }}>
                {INVOICE_HISTORY_FIELD_LABELS[field]}:{' '}
                <Box component='span' sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                  {formatInvoiceHistoryValue(field, String(original[FIELD_KEYS[field] as keyof FormValues] ?? ''))}
                </Box>
                {' → '}
                <Box component='span' sx={{ fontWeight: 600 }}>
                  {formatInvoiceHistoryValue(field, String(values[FIELD_KEYS[field] as keyof FormValues] ?? ''))}
                </Box>
              </Typography>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={onClose} color='secondary' disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleSave}
          disabled={!canSave}
          startIcon={isSaving && <CircularProgress size={18} color='inherit' />}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPastInvoiceDialog;
